import * as Sentry from '@sentry/react';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { camelCase, transform } from 'lodash';

import { useGetCurrentLoanApplicationID } from '@krea/common/hooks';
import { postFieldRequestData } from '@krea/common/store/field-requests/api';
import {
  CURRENT_LOAN_APPLICATION_STORE_KEY,
  ONE_FIELD_REQUEST_STORE_KEY,
  POST_FIELD_REQUEST_STORE_KEY,
  SELECTED_LENDER_RESPONSE_FIELD_REQUESTS_STORE_KEY,
} from '@krea/common/store/queryClient';
import { LOAN_APPLICATION_STATES } from '@krea/common/utils';

// TODO: Add strong types for the response
const usePostFieldRequest = (fieldRequestID: number) => {
  const queryClient = useQueryClient();
  const loanApplicationID = useGetCurrentLoanApplicationID();

  return useMutation({
    mutationKey: [POST_FIELD_REQUEST_STORE_KEY, fieldRequestID],
    mutationFn: async (value: object) => {
      const response = await postFieldRequestData({ fieldRequestID, value });
      const transformedData = transform(
        response?.data,
        (result: any, val, key) => {
          result[camelCase(key.toString())] = val;
        },
      );

      return { ...transformedData, field: camelCase(transformedData.field) };
    },
    onError: (err: any) => {
      if (err?.status !== 400) {
        console.error(
          'Failed posting field requests data err:',
          JSON.stringify(err),
        );
        Sentry.captureException(err);
      }
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(
        [ONE_FIELD_REQUEST_STORE_KEY, fieldRequestID],
        data,
      );

      const { state } = queryClient.getQueryData<any>([
        CURRENT_LOAN_APPLICATION_STORE_KEY,
        loanApplicationID,
      ]);

      if (state === LOAN_APPLICATION_STATES.BID_SELECTED) {
        await queryClient.invalidateQueries([
          SELECTED_LENDER_RESPONSE_FIELD_REQUESTS_STORE_KEY,
          loanApplicationID,
        ]);
      }
    },
  });
};

const useIsFieldRequestSubmitting = (fieldRequestID: number) => {
  const mutationKey = fieldRequestID
    ? [POST_FIELD_REQUEST_STORE_KEY, fieldRequestID]
    : [POST_FIELD_REQUEST_STORE_KEY];

  return useIsMutating({ mutationKey });
};

export { usePostFieldRequest, useIsFieldRequestSubmitting };
